import { useState } from "react";

import { Button, Card, ChevronDown, ChevronUp, cn } from "@app/ui";

import { useI18n } from "../../../lib/useI18n.js";
import { useMobileUx } from "../../../lib/useMobileUx.js";
import { useThemeStore } from "../../../stores/theme.store.js";
import { SettingsAnchor, SettingsCard } from "../SettingsCard.js";
import { SettingsSection } from "../SettingsSection.js";
import { EntityDefaultsEditor } from "./EntityDefaultsEditor.js";
import { PaletteBoardWithPreview } from "./PaletteBoardWithPreview.js";
import { PresetPickerCard } from "./PresetPickerCard.js";
import { RolePaletteEditor } from "./RolePaletteEditor.js";
import { SourceColorControls } from "./SourceColorControls.js";
import { ThemePreview } from "./ThemePreview.js";

/** Compact two-line sync toggle: title + description left, On/Off right. */
function SyncToggleCard() {
  const { t } = useI18n();
  const syncEnabled = useThemeStore((s) => s.syncEnabled);
  const setSyncEnabled = useThemeStore((s) => s.setSyncEnabled);
  return (
    <SettingsAnchor id="theme-sync">
      <Card className="p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-medium">{t("theme.syncTitle")}</h3>
            <p className="truncate text-xs text-muted-foreground">{t("theme.syncDescription")}</p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button size="sm" variant={syncEnabled ? "primary" : "outline"} onClick={() => void setSyncEnabled(true)}>{t("common.on")}</Button>
            <Button size="sm" variant={!syncEnabled ? "primary" : "outline"} onClick={() => void setSyncEnabled(false)}>{t("common.off")}</Button>
          </div>
        </div>
      </Card>
    </SettingsAnchor>
  );
}

/** Settings → UI Colors (formerly "Theme"). Order: preset + sync (compact) →
 *  the palette board with the live preview at its right, both pinned while
 *  scrolling on desktop (the board title stays in the normal flow so it
 *  scrolls away) → topic & calendar colors (painted from the board) →
 *  interface colors (generated from the material source) at the bottom.
 *  Mobile keeps the PREVIEW pinned instead (both pinned would eat the
 *  viewport). The board starts COLLAPSED here (unlike the Topics/Labels/
 *  Calendars tab, where it's the primary reason to visit) — expand via the
 *  Show/Hide toggle. */
export function ThemeSettings() {
  const { t } = useI18n();
  const mobile = useMobileUx();
  const [boardOpen, setBoardOpen] = useState(false);

  return (
    <div className="flex flex-col gap-6">
      {mobile && (
        <div className="sticky top-0 z-10 -mt-1 bg-background pb-2 pt-1">
          <ThemePreview />
        </div>
      )}

      <SettingsSection title={t("theme.title")}>
        <PresetPickerCard />
        <SyncToggleCard />
      </SettingsSection>

      {/* Heading outside the sticky wrapper so it scrolls away while the board
          stays pinned. The sticky wrapper must remain a DIRECT child of the
          page column — nesting it deeper would end its sticking early. */}
      <div className="-mb-4 flex items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t("theme.paletteBoard")}
        </h2>
        <Button variant="ghost" size="sm" onClick={() => setBoardOpen((open) => !open)} aria-expanded={boardOpen}>
          {boardOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          <span className="ml-1">{boardOpen ? t("common.hide") : t("common.show")}</span>
        </Button>
      </div>
      {boardOpen && (
        <div className={cn(!mobile && "sticky top-0 z-10 bg-background pb-2")}>
          <PaletteBoardWithPreview
            boardAnchorId="theme-board"
            previewAnchorId="theme-preview"
            defaultTarget={{ kind: "entity", target: { kind: "global" } }}
          />
        </div>
      )}

      <SettingsSection title={t("theme.entityColors")} description={t("theme.entityColors.description")}>
        <SettingsCard id="theme-entity" title={t("theme.entity")} description={t("theme.entity.description")}>
          <EntityDefaultsEditor />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection title={t("theme.uiColors")} description={t("theme.uiColors.description")}>
        <SettingsCard id="theme-source" title={t("theme.source")} description={t("theme.source.description")}>
          <SourceColorControls />
        </SettingsCard>

        <SettingsCard id="theme-roles" title={t("theme.roles")} description={t("theme.roles.description")}>
          <RolePaletteEditor />
        </SettingsCard>
      </SettingsSection>
    </div>
  );
}
